"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { cn } from "@/lib/utils";
import { useIsScreenMd } from "hooks/useIsScreenMd";
import { useMobileSidebarStore } from "stores/sidebar/useMobileSidebarStore";
import { DropdownAuroraGlow } from "../composites/dropdown-aurora-glow";
import { LiquidPill } from "../liquid-pill";
import { V2_MOTION } from "../motion";
import { Z_INDEX } from "../z-index";
import { SidebarExplorePanel } from "./sidebar-explore-panel";

export function SidebarMobileDrawer() {
  const isScreenMd = useIsScreenMd();
  const { isOpenBurger, closeBurgerSidebar } = useMobileSidebarStore();
  const { t } = useTranslation("common");

  const [mounted, setMounted] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);

  const prefersReducedMotion = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted || isScreenMd) return;

    if (isOpenBurger) {
      setShouldRender(true);
      setIsVisible(false);

      if (prefersReducedMotion) {
        setIsVisible(true);
        return;
      }

      const openId = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setIsVisible(true));
      });
      return () => window.cancelAnimationFrame(openId);
    }

    setIsVisible(false);
    const closeId = window.setTimeout(
      () => setShouldRender(false),
      prefersReducedMotion ? 0 : V2_MOTION.duration.revealClose + 60
    );
    return () => window.clearTimeout(closeId);
  }, [isOpenBurger, isScreenMd, mounted, prefersReducedMotion]);

  React.useEffect(() => {
    if (!shouldRender || isScreenMd) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isScreenMd, shouldRender]);

  React.useEffect(() => {
    if (!shouldRender || isScreenMd) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeBurgerSidebar();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeBurgerSidebar, isScreenMd, shouldRender]);

  if (!mounted || isScreenMd || !shouldRender) return null;

  const motionDuration = isVisible
    ? V2_MOTION.duration.revealOpen
    : V2_MOTION.duration.revealClose;
  const motionEase = isVisible
    ? V2_MOTION.ease.premiumReveal
    : V2_MOTION.ease.standard;

  return createPortal(
    <div
      className="fixed inset-0"
      style={{ zIndex: Z_INDEX.DRAWER }}
      aria-hidden={!isVisible}
    >
      <button
        type="button"
        aria-label={t("Cerrar menú")}
        className="absolute inset-0 z-0 bg-white/55 backdrop-blur-[10px] dark:bg-zinc-950/55"
        style={{
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? "auto" : "none",
          transition: `opacity ${motionDuration}ms ${motionEase}`,
        }}
        onClick={closeBurgerSidebar}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t("Menú de navegación")}
        className={cn(
          "absolute inset-y-0 left-0 z-10 flex w-[min(84vw,280px)] max-w-full flex-col overflow-hidden",
          "rounded-r-[22px] border border-slate-200/60 bg-white/82 shadow-[0_28px_90px_-24px_rgba(15,23,42,0.38),0_18px_48px_-18px_rgba(79,70,229,0.16),inset_0_1px_0_rgba(255,255,255,0.72)] backdrop-blur-2xl",
          "dark:border-zinc-800/70 dark:bg-zinc-950/84 dark:shadow-[0_28px_90px_-24px_rgba(0,0,0,0.62),0_18px_48px_-18px_rgba(99,102,241,0.18),inset_0_1px_0_rgba(255,255,255,0.05)]"
        )}
        style={{
          transform: isVisible ? "translateX(0)" : "translateX(-104%)",
          opacity: isVisible ? 1 : 0.94,
          filter: isVisible ? "blur(0px) saturate(1)" : "blur(8px) saturate(1.04)",
          transition: [
            `transform ${motionDuration}ms ${motionEase}`,
            `opacity ${motionDuration}ms ${motionEase}`,
            `filter ${motionDuration}ms ${motionEase}`,
          ].join(", "),
        }}
      >
        <div className="pointer-events-none absolute inset-0 rounded-r-[inherit] border border-white/45 dark:border-white/5" />
        <DropdownAuroraGlow />

        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200/50 px-3 py-2 dark:border-zinc-800/70">
            <h2 className="text-[13px] font-semibold leading-none text-slate-900 dark:text-zinc-50">
              {t("Menú")}
            </h2>
            <LiquidPill
              as="button"
              type="button"
              onClick={closeBurgerSidebar}
              aria-label={t("Cerrar menú")}
              variant="rose"
              appearance="tinted"
              shape="circle"
              size="sm"
              icon={<X className="h-3.5 w-3.5" />}
            />
          </div>

          <SidebarExplorePanel embedded />
        </div>
      </aside>
    </div>,
    document.body
  );
}
